import EventEmitter from 'eventemitter3';
import createDebug from 'debug';
import { getSizeInMB } from '@/functions';
import { EventNames, Size } from '@/constants';

// See the Debug Logging section of the ML's README.md for how to use this.
const debug = createDebug('durable:RemainingRecordedVideoData');

const { READY_TO_UPLOAD } = EventNames.RemainingRecordedVideoData;

const { ONE_MB } = Size;

/** @typedef {{ videoChunkSize: number; }} RemainingRecordedVideoDataOptions */

/** @type {RemainingRecordedVideoDataOptions} */
const defaultOptions = {
  videoChunkSize: ONE_MB,
};

/**
 * Stores video data that has been recorded, allows for getting chunks of video data to upload, and
 * emits an event when there is enough data to upload.
 */
export class RemainingRecordedVideoData extends EventEmitter {
  totalVideoSizeInBytes = 0;

  videoData = new Blob();

  videoChunkSize = ONE_MB;

  /**
   * @param {RemainingRecordedVideoDataOptions} options
   */
  constructor(options = defaultOptions) {
    super();

    const fullOptions = {
      ...defaultOptions,
      ...options,
    };

    this.videoChunkSize = fullOptions.videoChunkSize;
  }

  /**
   * @returns {number} The total size of the video data in bytes
   */
  get size() {
    return this.videoData.size;
  }

  /**
   * @returns {number} The total size of the video data in megabytes
   */
  get sizeInMB() {
    return getSizeInMB(this.size);
  }

  /**
   * @param {Blob} newVideoData A chunk of video data to add to the current remaining video data
   */
  addVideoData(newVideoData) {
    this.totalVideoSizeInBytes += newVideoData.size;
    this.videoData = new Blob([this.videoData, newVideoData]);
    const methodDebug = debug.extend('addVideoData');
    methodDebug('After adding new data, size in MB is:', this.sizeInMB);

    if (this.hasEnoughDataToUpload) {
      methodDebug('Has enough data to upload, emitting readyToUpload');
      this.emit(READY_TO_UPLOAD);
    }
  }

  /**
   * Destroy video data
   */
  dispose() {
    this.videoData = new Blob();
  }

  /**
   * @returns {Blob} A chunk of video data whose size is equal to the videoChunkSize
   */
  getVideoDataChunk() {
    const videoChunk = this.videoData.slice(0, this.videoChunkSize);
    this.videoData = this.videoData.slice(this.videoChunkSize);

    return videoChunk;
  }

  /**
   * @returns {boolean}
   */
  get hasEnoughDataToUpload() {
    return this.videoData.size >= this.videoChunkSize;
  }

  /**
   * @returns {boolean}
   */
  get hasRemainingData() {
    return this.videoData.size > 0;
  }
}
